/**
 * Métadonnées d'affichage pour l'État de la procédure (ADR-026).
 *
 * Toutes les valeurs de référence côté UI : libellés avocat, libellés
 * client (déjuridicisés), couleurs de phase, ordre, mapping
 * event_type → phase d'appartenance, libellés de délais dérivés.
 */

import type { DeadlineKind, DossierRole, ProcedureEventType, ProcedurePhase } from './types';

export const PHASE_ORDER: ProcedurePhase[] = [
  'preliminaire',
  'instruction',
  'reglement',
  'fond_correctionnel',
  'fond_assises',
  'appel_correctionnel',
  'cassation',
  'execution',
  'clos',
];

export const PHASE_LABELS: Record<ProcedurePhase, { lawyer: string; client: string }> = {
  preliminaire: {
    lawyer: 'Préliminaire (parquet)',
    client: 'Enquête par le procureur',
  },
  instruction: {
    lawyer: "Instruction (juge d'instruction)",
    client: 'Enquête approfondie par un juge',
  },
  reglement: {
    lawyer: 'Règlement de la procédure (chambre du conseil)',
    client: 'Décision sur la suite : procès ou non-lieu',
  },
  fond_correctionnel: {
    lawyer: 'Fond — Tribunal correctionnel',
    client: 'Procès devant le tribunal',
  },
  fond_assises: {
    lawyer: "Fond — Cour d'assises",
    client: "Procès devant la cour d'assises (jury populaire)",
  },
  appel_correctionnel: {
    lawyer: "Appel — Cour d'appel",
    client: "Recours devant la cour d'appel",
  },
  cassation: {
    lawyer: 'Cassation',
    client: 'Recours en cassation (légalité)',
  },
  execution: {
    lawyer: 'Exécution de la peine',
    client: 'Exécution de la décision',
  },
  clos: {
    lawyer: 'Clos / définitif',
    client: 'Dossier clôturé',
  },
};

export const ROLE_LABELS: Record<DossierRole, { short: string; long: string }> = {
  suspect_prevenu: { short: 'Défense', long: 'Suspect / inculpé / prévenu (défense)' },
  partie_civile: { short: 'Partie civile', long: 'Partie civile / victime' },
};

type EventMeta = {
  phase: ProcedurePhase;
  lawyer: string;
  client: string;
  /** Rôles pour lesquels cet événement est pertinent ; ``both`` pour les
   *  événements communs (audience, jugement). */
  relevant: 'both' | DossierRole;
};

export const EVENT_META: Record<ProcedureEventType, EventMeta> = {
  // Préliminaire
  plainte: {
    phase: 'preliminaire',
    lawyer: 'Plainte déposée',
    client: 'Plainte déposée auprès de la police ou du parquet',
    relevant: 'both',
  },
  constitution_pc_parquet: {
    phase: 'preliminaire',
    lawyer: 'Constitution PC (parquet — art 5bis Franchimont)',
    client: 'Constitution comme partie civile, devant le parquet',
    relevant: 'partie_civile',
  },
  information_ouverte: {
    phase: 'preliminaire',
    lawyer: 'Information ouverte (parquet)',
    client: 'Le procureur a ouvert une enquête',
    relevant: 'both',
  },
  requisitoire_mise_instruction: {
    phase: 'preliminaire',
    lawyer: "Réquisitoire de mise à l'instruction",
    client: "Le parquet a saisi un juge d'instruction",
    relevant: 'both',
  },
  // Instruction
  inculpation: {
    phase: 'instruction',
    lawyer: "Inculpation par le juge d'instruction",
    client: 'Mise en cause formelle par le juge',
    relevant: 'suspect_prevenu',
  },
  mandat_arret: {
    phase: 'instruction',
    lawyer: "Mandat d'arrêt (détention préventive)",
    client: 'Placement en détention provisoire',
    relevant: 'suspect_prevenu',
  },
  mandat_amener: {
    phase: 'instruction',
    lawyer: "Mandat d'amener",
    client: 'Convocation forcée devant le juge',
    relevant: 'suspect_prevenu',
  },
  audition_inculpe: {
    phase: 'instruction',
    lawyer: "Audition par le juge d'instruction",
    client: 'Audition par le juge',
    relevant: 'suspect_prevenu',
  },
  cc_passage_initial: {
    phase: 'instruction',
    lawyer: 'Chambre du conseil — passage initial (J+5)',
    client: 'Premier passage en chambre du conseil (contrôle de la détention)',
    relevant: 'suspect_prevenu',
  },
  cc_controle_detention: {
    phase: 'instruction',
    lawyer: 'Chambre du conseil — contrôle mensuel détention',
    client: 'Contrôle mensuel de la détention',
    relevant: 'suspect_prevenu',
  },
  cma_appel_detention: {
    phase: 'instruction',
    lawyer: 'CMA — appel détention',
    client: 'Recours sur la détention devant la chambre des mises en accusation',
    relevant: 'suspect_prevenu',
  },
  libere_sous_conditions: {
    phase: 'instruction',
    lawyer: 'Libération sous conditions',
    client: 'Libération avec conditions à respecter',
    relevant: 'suspect_prevenu',
  },
  libere: {
    phase: 'instruction',
    lawyer: 'Libération',
    client: 'Libération',
    relevant: 'suspect_prevenu',
  },
  requete_61ter: {
    phase: 'instruction',
    lawyer: 'Requête art. 61ter — accès au dossier',
    client: "Demande d'accès au dossier d'instruction",
    relevant: 'both',
  },
  ordonnance_61ter: {
    phase: 'instruction',
    lawyer: 'Ordonnance art. 61ter',
    client: "Décision du juge sur l'accès au dossier",
    relevant: 'both',
  },
  requete_61quater: {
    phase: 'instruction',
    lawyer: "Requête art. 61quater — levée d'un acte",
    client: "Demande de levée d'un acte d'enquête (saisie, écoute…)",
    relevant: 'both',
  },
  ordonnance_61quater: {
    phase: 'instruction',
    lawyer: 'Ordonnance art. 61quater',
    client: 'Décision du juge sur la levée demandée',
    relevant: 'both',
  },
  requete_61quinquies: {
    phase: 'instruction',
    lawyer: 'Requête art. 61quinquies — devoir complémentaire',
    client: "Demande d'un acte d'enquête supplémentaire",
    relevant: 'both',
  },
  ordonnance_61quinquies: {
    phase: 'instruction',
    lawyer: 'Ordonnance art. 61quinquies',
    client: "Décision du juge sur l'acte demandé",
    relevant: 'both',
  },
  avis_fin_instruction: {
    phase: 'instruction',
    lawyer: "Avis de fin d'instruction (art. 127)",
    client: 'Le juge annonce la fin de son enquête',
    relevant: 'both',
  },
  conclusions_127: {
    phase: 'instruction',
    lawyer: 'Conclusions art. 127 §3',
    client: 'Observations écrites avant règlement de la procédure',
    relevant: 'both',
  },
  requisitoire_final: {
    phase: 'instruction',
    lawyer: 'Réquisitoire final du parquet',
    client: 'Demande finale du parquet (renvoi, non-lieu, etc.)',
    relevant: 'both',
  },
  // Règlement
  cc_audience_reglement: {
    phase: 'reglement',
    lawyer: 'Chambre du conseil — audience règlement',
    client: 'Audience qui décide si le dossier va au tribunal',
    relevant: 'both',
  },
  cc_ordonnance_renvoi_corr: {
    phase: 'reglement',
    lawyer: 'Ordonnance — renvoi correctionnel',
    client: 'Renvoi devant le tribunal correctionnel',
    relevant: 'both',
  },
  cc_ordonnance_renvoi_assises: {
    phase: 'reglement',
    lawyer: "Ordonnance — renvoi cour d'assises",
    client: "Renvoi devant la cour d'assises",
    relevant: 'both',
  },
  cc_ordonnance_non_lieu: {
    phase: 'reglement',
    lawyer: 'Ordonnance — non-lieu',
    client: 'Non-lieu : pas de poursuites',
    relevant: 'both',
  },
  cc_ordonnance_dessaisissement: {
    phase: 'reglement',
    lawyer: 'Ordonnance — dessaisissement',
    client: 'Le juge se dessaisit du dossier',
    relevant: 'both',
  },
  cma_appel_ordonnance: {
    phase: 'reglement',
    lawyer: "CMA — appel de l'ordonnance",
    client: "Recours contre l'ordonnance de règlement",
    relevant: 'both',
  },
  cma_arret_reglement: {
    phase: 'reglement',
    lawyer: 'CMA — arrêt de règlement',
    client: 'Décision de la chambre des mises en accusation',
    relevant: 'both',
  },
  // Fond correctionnel
  citation_corr: {
    phase: 'fond_correctionnel',
    lawyer: 'Citation — tribunal correctionnel',
    client: 'Convocation au tribunal',
    relevant: 'both',
  },
  audience_intro_corr: {
    phase: 'fond_correctionnel',
    lawyer: "Audience d'introduction",
    client: 'Première audience (mise en état du dossier)',
    relevant: 'both',
  },
  depot_conclusions_corr: {
    phase: 'fond_correctionnel',
    lawyer: 'Dépôt de conclusions',
    client: 'Dépôt des conclusions écrites',
    relevant: 'both',
  },
  audience_debats_corr: {
    phase: 'fond_correctionnel',
    lawyer: 'Audience des débats (plaidoiries)',
    client: 'Audience de plaidoiries',
    relevant: 'both',
  },
  jugement_corr_contradictoire: {
    phase: 'fond_correctionnel',
    lawyer: 'Jugement correctionnel — contradictoire',
    client: 'Jugement rendu après audience',
    relevant: 'both',
  },
  jugement_corr_defaut: {
    phase: 'fond_correctionnel',
    lawyer: 'Jugement correctionnel — défaut',
    client: 'Jugement rendu sans présence de la défense',
    relevant: 'both',
  },
  signification_jugement: {
    phase: 'fond_correctionnel',
    lawyer: 'Signification du jugement',
    client: 'Remise officielle du jugement (par huissier)',
    relevant: 'both',
  },
  opposition: {
    phase: 'fond_correctionnel',
    lawyer: 'Opposition (jugement défaut)',
    client: 'Opposition au jugement rendu par défaut',
    relevant: 'both',
  },
  // Assises
  mise_accusation: {
    phase: 'fond_assises',
    lawyer: 'Mise en accusation',
    client: "Mise en accusation devant la cour d'assises",
    relevant: 'suspect_prevenu',
  },
  audience_ouverture_assises: {
    phase: 'fond_assises',
    lawyer: "Audience d'ouverture des assises",
    client: 'Ouverture du procès aux assises',
    relevant: 'both',
  },
  verdict_assises: {
    phase: 'fond_assises',
    lawyer: 'Verdict du jury',
    client: 'Verdict du jury populaire',
    relevant: 'both',
  },
  arret_assises: {
    phase: 'fond_assises',
    lawyer: "Arrêt cour d'assises",
    client: "Arrêt rendu par la cour d'assises",
    relevant: 'both',
  },
  // Appel correctionnel
  acte_appel: {
    phase: 'appel_correctionnel',
    lawyer: "Acte d'appel",
    client: "Déclaration d'appel",
    relevant: 'both',
  },
  citation_ca: {
    phase: 'appel_correctionnel',
    lawyer: "Citation — cour d'appel",
    client: "Convocation devant la cour d'appel",
    relevant: 'both',
  },
  audience_intro_ca: {
    phase: 'appel_correctionnel',
    lawyer: "Audience d'introduction CA",
    client: 'Première audience en appel',
    relevant: 'both',
  },
  audience_debats_ca: {
    phase: 'appel_correctionnel',
    lawyer: 'Audience des débats CA',
    client: 'Audience de plaidoiries en appel',
    relevant: 'both',
  },
  arret_ca: {
    phase: 'appel_correctionnel',
    lawyer: "Arrêt cour d'appel",
    client: "Arrêt rendu par la cour d'appel",
    relevant: 'both',
  },
  // Cassation
  pourvoi_cassation: {
    phase: 'cassation',
    lawyer: 'Pourvoi en cassation',
    client: 'Recours en cassation',
    relevant: 'both',
  },
  memoire_cassation: {
    phase: 'cassation',
    lawyer: 'Mémoire en cassation',
    client: "Dépôt du mémoire à l'appui du pourvoi",
    relevant: 'both',
  },
  arret_cassation: {
    phase: 'cassation',
    lawyer: 'Arrêt Cour de cassation',
    client: 'Décision de la Cour de cassation',
    relevant: 'both',
  },
  // Exécution
  signification_definitive: {
    phase: 'execution',
    lawyer: 'Signification définitive',
    client: 'Notification définitive de la décision',
    relevant: 'both',
  },
  ecrou: {
    phase: 'execution',
    lawyer: 'Écrou (incarcération)',
    client: 'Incarcération pour exécution de la peine',
    relevant: 'suspect_prevenu',
  },
  liberation_definitive: {
    phase: 'execution',
    lawyer: 'Libération définitive',
    client: 'Fin de la peine',
    relevant: 'suspect_prevenu',
  },
  liberation_conditionnelle: {
    phase: 'execution',
    lawyer: 'Libération conditionnelle',
    client: 'Libération conditionnelle',
    relevant: 'suspect_prevenu',
  },
  autre: {
    phase: 'preliminaire',
    lawyer: 'Autre événement',
    client: 'Autre événement',
    relevant: 'both',
  },
};

export const DEADLINE_LABELS: Record<DeadlineKind, { lawyer: string; client: string }> = {
  cc_initial_5j: {
    lawyer: 'Passage CC initial (J+5)',
    client: 'Premier passage en chambre du conseil dans 5 jours',
  },
  cma_detention_24h: {
    lawyer: 'Appel CMA détention (J+24h)',
    client: 'Recours sur la détention possible dans les 24h',
  },
  appel_cma_reglement_15j: {
    lawyer: 'Appel CMA règlement (J+15)',
    client: 'Recours possible dans les 15 jours',
  },
  opposition_15j: {
    lawyer: 'Opposition (J+15 après signification)',
    client: 'Opposition possible dans les 15 jours après remise par huissier',
  },
  appel_corr_30j: {
    lawyer: 'Appel correctionnel (J+30)',
    client: 'Appel possible dans les 30 jours',
  },
  cassation_15j: {
    lawyer: 'Pourvoi en cassation (J+15)',
    client: 'Pourvoi en cassation possible dans les 15 jours',
  },
  appel_assises_30j: {
    lawyer: 'Appel assises (J+30)',
    client: 'Appel possible dans les 30 jours',
  },
};

export function eventLabel(
  type: ProcedureEventType,
  mode: 'lawyer' | 'client',
  override?: string | null
): string {
  if (override && override.trim().length > 0) return override;
  return EVENT_META[type][mode];
}

export function phaseLabel(phase: ProcedurePhase, mode: 'lawyer' | 'client'): string {
  return PHASE_LABELS[phase][mode];
}

export function deadlineLabel(kind: DeadlineKind, mode: 'lawyer' | 'client'): string {
  return DEADLINE_LABELS[kind][mode];
}

export function filterEventsByRole<T extends { event_type: ProcedureEventType }>(
  events: T[],
  role: DossierRole
): T[] {
  return events.filter((e) => {
    const meta = EVENT_META[e.event_type];
    return meta.relevant === 'both' || meta.relevant === role;
  });
}
